import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

// Styled replacement for a native <select>, matching OrgSwitcher's menu so the
// two read as one system. A native select renders with the OS's own popup, which
// ignores the app's theme entirely.
//
// Keyboard behaviour is deliberate, not decoration: a native select is navigable
// by arrows and type-ahead, so a custom one that only responds to clicks is a
// regression for anyone not using a mouse.
export default function Select({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  className = '',
  disabled = false,
  align = 'left',
}) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [box, setBox] = useState(null);
  const ref = useRef(null);
  const listRef = useRef(null);

  // The menu renders into document.body, not beside the button. Any ancestor
  // with overflow clips an absolutely positioned child, and this control sits
  // inside scrolling cards and tables — the menu was being cut off, so options
  // could only be reached by scrolling the container behind it.
  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const flip = below < 240 && r.top > below;   // open upward when it fits better
    setBox({
      left: r.left,
      width: r.width,
      ...(flip ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      maxHeight: Math.max(160, (flip ? r.top : below) - 16),
    });
  }, []);

  const current = options.find((o) => o.value === value);
  const label = current?.label ?? (value || placeholder);

  const close = useCallback(() => { setOpen(false); setCursor(-1); }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      close();
    };
    // Reposition rather than drift: the menu is fixed, so a scroll behind it
    // would otherwise leave it floating away from its button.
    place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, close, place]);

  // Open at the current selection rather than the top, so a 49-model list does
  // not start scrolled away from what is already chosen.
  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value);
    setCursor(idx >= 0 ? idx : 0);
  }, [open, options, value]);

  useEffect(() => {
    if (!open || cursor < 0 || !listRef.current) return;
    listRef.current.children[cursor]?.scrollIntoView({ block: 'nearest' });
  }, [open, cursor]);

  const pick = (option) => { onChange(option.value); close(); };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault(); setOpen(true); return;
    }
    if (!open) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && options[cursor]) { e.preventDefault(); pick(options[cursor]); }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { if (!disabled) { place(); setOpen((v) => !v); } }}
        onKeyDown={onKeyDown}
        className={`w-full flex items-center gap-2 px-3 py-1.5 bg-field border rounded text-left
          text-sm transition-colors ${open ? 'border-orange' : 'border-field-border'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-orange/60'}`}
      >
        <span className={`truncate flex-1 ${current ? 'text-fg' : 'text-fg-muted'}`}>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-fg-muted shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && box && createPortal(
        <div
          role="listbox"
          ref={listRef}
          style={{ position: 'fixed', ...box, overflowY: 'auto' }}
          className="z-[100] bg-panel border border-panel-border rounded-lg shadow-lg py-1"
        >
          {options.length === 0 && (
            <div className="px-3 py-2 text-sm text-fg-muted">Nothing to choose</div>
          )}
          {options.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => pick(o)}
              onMouseEnter={() => setCursor(i)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                ${i === cursor ? 'bg-fg/5' : ''} ${o.value === value ? 'text-fg' : 'text-fg-muted'}
                hover:text-fg`}
            >
              <span className="truncate flex-1">{o.label}</span>
              {o.hint && <span className="text-[10px] text-fg-muted shrink-0">{o.hint}</span>}
              {o.value === value && <Check className="w-4 h-4 text-orange shrink-0" />}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
